import React, { Fragment, forwardRef, useEffect } from 'react';
import Type from 'prop-types';
import {
  AutoSizer,
  List,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import { Divider, List as MuiList, ListSubheader } from "@mui/material";
import { groupByDates, getRelativeDateTime } from 'utils/date';
import PromiseItem from '../PromiseItem';
import styles from './PromiseList.module.css';

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 200,
});

const PromiseList = forwardRef((props, ref) => {
  const { promises: promisesProp, listHeight } = props;
  const promiseDates = groupByDates(promisesProp, 'createdAt');
  const rowCount = promiseDates.length;

  useEffect(() => {
    cache.clearAll();
  }, [promisesProp]);

  const rowRenderer = (rowData) => {
    const { index, parent, key, style } = rowData;
    const [promiseDate, promises] = promiseDates[index];
    const { date: formattedPromiseDate } = getRelativeDateTime(promiseDate);

    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        <div style={style}>
          <MuiList
            classes={{ root: styles.list }}
            subheader={(
              <ListSubheader component="div">
                {formattedPromiseDate}
              </ListSubheader>
            )}
            disablePadding
          >
            {promises.map((operation, idx) => {
              return (
                <Fragment key={operation.id}>
                  <PromiseItem promise={operation} />

                  {promises.length - 1 !== idx && <Divider />}
                </Fragment>
              );
            })}
          </MuiList>
        </div>
      </CellMeasurer>
    );
  };

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <List
          ref={ref}
          height={listHeight}
          overscanRowCount={10}
          rowCount={rowCount}
          deferredMeasurementCache={cache}
          rowHeight={cache.rowHeight}
          rowRenderer={rowRenderer}
          width={width}
        />
      )}
    </AutoSizer>
  );
});

PromiseList.propTypes = {
  listHeight: Type.number.isRequired,
  promises: Type.arrayOf(Type.shape(PromiseItem.propTypes.operation))
    .isRequired,
};

export default PromiseList;
